import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getApiUrl } from "../../api";

interface MemberInfo {
  name: string;
  phone: string;
  email: string;
  affiliation_name: string;
  grade: string;
}

interface TimeSlot {
  id: number;
  start_time: string;
  end_time: string;
  max_count: number;
  is_active: number;
}

interface ConsentPolicy {
  id: number;
  content: string;
  title: string;
}

export default function CounselApply() {
  const navigate = useNavigate();
  const [member, setMember] = useState<MemberInfo | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
  const [consentPolicy, setConsentPolicy] = useState<ConsentPolicy | null>(null);

  const [selectedSlotId, setSelectedSlotId] = useState<number | null>(null);
  const [scheduledDate, setScheduledDate] = useState("");
  const [infoChecked, setInfoChecked] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [memo, setMemo] = useState("");

  // 비회원 입력 필드
  const [guestName, setGuestName] = useState("");
  const [guestPhone, setGuestPhone] = useState("");
  const [guestAffiliation, setGuestAffiliation] = useState("");

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [alertModal, setAlertModal] = useState<string | null>(null);
  const [successModal, setSuccessModal] = useState(false);

  const today = new Date().toISOString().split("T")[0];

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem("access_token");

        const [slotsRes, consentRes] = await Promise.all([
          fetch(getApiUrl("/marubit/api/counseling/slots?is_active=1")),
          fetch(getApiUrl("/marubit/api/consent-policy/detail?consent_type=counseling")),
        ]);

        if (slotsRes.ok) setTimeSlots(await slotsRes.json());
        if (consentRes.ok) setConsentPolicy(await consentRes.json());

        if (token) {
          const meRes = await fetch(getApiUrl("/marubit/api/member/me"), {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (meRes.ok) {
            const meData = await meRes.json();
            setMember({
              name: meData.name ?? "",
              phone: meData.phone ?? "",
              email: meData.email ?? "",
              affiliation_name: meData.affiliation_name ?? "",
              grade: meData.grade ?? "",
            });
            setIsLoggedIn(true);
          }
        }
      } catch {
        // 슬롯/동의서 실패해도 페이지는 유지
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const formatTime = (t: string) => t.slice(0, 5);

  const handleSubmit = async () => {
    if (!isLoggedIn) {
      if (!guestName.trim()) { setAlertModal("이름을 입력해주세요."); return; }
      if (!guestPhone.trim()) { setAlertModal("전화번호를 입력해주세요."); return; }
    }
    if (!infoChecked) { setAlertModal("정보가 맞는지 확인 후 체크해주세요."); return; }
    if (!scheduledDate) { setAlertModal("상담 날짜를 선택해주세요."); return; }
    if (!selectedSlotId) { setAlertModal("상담 시간을 선택해주세요."); return; }
    if (!consentChecked) { setAlertModal("개인정보 수집 및 이용에 동의해주세요."); return; }
    if (!consentPolicy) { setAlertModal("동의 정책 정보를 불러올 수 없습니다. 새로고침 후 다시 시도해주세요."); return; }

    setSubmitting(true);
    try {
      const token = localStorage.getItem("access_token");

      if (isLoggedIn && token) {
        // 회원 신청
        const res = await fetch(getApiUrl("/marubit/api/counseling/applications"), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            time_slot_id: selectedSlotId,
            scheduled_date: scheduledDate,
            memo: memo || null,
            consent_policy_id: consentPolicy.id,
            consent_agreed: 1,
          }),
        });
        if (!res.ok) {
          const err = await res.json();
          setAlertModal(err.detail ?? "신청 중 오류가 발생했습니다.");
          return;
        }
      } else {
        // 비회원 신청
        const res = await fetch(getApiUrl("/marubit/api/counseling/guest/applications"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            guest_name: guestName,
            guest_phone: guestPhone,
            guest_affiliation: guestAffiliation || null,
            time_slot_id: selectedSlotId,
            scheduled_date: scheduledDate,
            memo: memo || null,
            consent_policy_id: consentPolicy.id,
            consent_agreed: 1,
          }),
        });
        if (!res.ok) {
          const err = await res.json();
          setAlertModal(err.detail ?? "신청 중 오류가 발생했습니다.");
          return;
        }
      }

      setSuccessModal(true);
    } catch {
      setAlertModal("신청 중 오류가 발생했습니다. 다시 시도해주세요.");
    } finally {
      setSubmitting(false);
    }
  };

  const InfoRow = ({ label, value }: { label: string; value: string }) => (
    <div className="flex items-center py-[14px] border-b border-gray-100">
      <span className="w-[100px] text-[13px] text-gray-400 font-medium shrink-0">{label}</span>
      <span className="text-[14px] text-[#222] font-medium">{value || "-"}</span>
    </div>
  );

  if (loading) return (
    <div className="min-h-[60vh] flex items-center justify-center text-gray-300">
      불러오는 중...
    </div>
  );

  return (
    <div className="bg-[#fafafa] min-h-screen px-6 py-12">
      <div className="max-w-[1200px] mx-auto">

        {/* 페이지 타이틀 */}
        <div className="mb-8">
          <h1 className="m-0 mb-2 text-[26px] font-extrabold text-[#5a4035]">상담 신청서</h1>
          <p className="m-0 text-[14px] text-gray-400">아래 정보를 확인하고 상담을 신청해주세요.</p>
        </div>

        {/* 신청자 정보 */}
        <div className="bg-white rounded-2xl p-7 mb-4 shadow-[0_2px_12px_rgba(136,114,103,0.06)]">
          <h2 className="m-0 mb-1 text-[16px] font-bold text-[#887267] border-l-[3px] border-[#887267] pl-[10px]">
            신청자 정보
          </h2>

          {isLoggedIn && member ? (
            <>
              <p className="mt-0 mb-4 ml-[13px] text-[12px] text-gray-300">로그인한 계정의 정보입니다.</p>
              <InfoRow label="이름" value={member.name} />
              <InfoRow label="전화번호" value={member.phone} />
              <InfoRow label="이메일" value={member.email} />
              <InfoRow label="소속명" value={member.affiliation_name} />
              <InfoRow label="학년" value={member.grade} />
              <div className="flex items-center justify-between mt-5 px-4 py-[14px] bg-[#f5f2f0] rounded-[10px] border border-[#e8e0dc]">
                <label className="flex items-center gap-[10px] cursor-pointer">
                  <input
                    type="checkbox"
                    checked={infoChecked}
                    onChange={e => setInfoChecked(e.target.checked)}
                    className="w-4 h-4 cursor-pointer accent-[#887267]"
                  />
                  <span className="text-[13px] font-semibold text-[#887267]">내 정보가 맞습니다 <span className="text-red-500">(필수)</span></span>
                </label>
                <button
                  onClick={() => navigate("/mypage/edit")}
                  className="px-[14px] py-[6px] text-[12px] font-semibold text-[#887267] bg-white border-[1.5px] border-[#887267] rounded-[6px] cursor-pointer transition-all duration-150 hover:bg-[#887267] hover:text-white"
                >
                  내 정보 수정
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="mt-0 mb-4 ml-[13px] text-[12px] text-gray-300">비회원으로 신청합니다.</p>
              <div className="space-y-3">
                <input
                  type="text"
                  placeholder="이름 *"
                  value={guestName}
                  onChange={e => setGuestName(e.target.value)}
                  className="w-full box-border px-4 py-3 text-[14px] border-[1.5px] border-gray-200 rounded-[10px] outline-none focus:border-[#887267] transition-colors duration-150"
                />
                <input
                  type="tel"
                  placeholder="전화번호 *"
                  value={guestPhone}
                  onChange={e => setGuestPhone(e.target.value)}
                  className="w-full box-border px-4 py-3 text-[14px] border-[1.5px] border-gray-200 rounded-[10px] outline-none focus:border-[#887267] transition-colors duration-150"
                />
                <input
                  type="text"
                  placeholder="소속 (선택)"
                  value={guestAffiliation}
                  onChange={e => setGuestAffiliation(e.target.value)}
                  className="w-full box-border px-4 py-3 text-[14px] border-[1.5px] border-gray-200 rounded-[10px] outline-none focus:border-[#887267] transition-colors duration-150"
                />
              </div>
              <div className="flex items-center gap-3 mt-4 px-4 py-[14px] bg-[#f5f2f0] rounded-[10px] border border-[#e8e0dc]">
                <input
                  type="checkbox"
                  checked={infoChecked}
                  onChange={e => setInfoChecked(e.target.checked)}
                  className="w-4 h-4 cursor-pointer accent-[#887267]"
                />
                <span className="text-[13px] font-semibold text-[#887267]">입력한 정보가 맞습니다 <span className="text-red-500">(필수)</span></span>
              </div>
              <div className="mt-3 text-center">
                <button
                  onClick={() => navigate("/auth/login")}
                  className="text-[13px] text-[#887267] underline"
                >
                  회원이신가요? 로그인하기
                </button>
              </div>
            </>
          )}
        </div>

        {/* 상담 일정 선택 */}
        <div className="bg-white rounded-2xl p-7 mb-4 shadow-[0_2px_12px_rgba(136,114,103,0.06)]">
          <h2 className="m-0 mb-1 text-[16px] font-bold text-[#887267] border-l-[3px] border-[#887267] pl-[10px]">
            상담 일정 선택
          </h2>
          <p className="mt-0 mb-4 ml-[13px] text-[12px] text-gray-300">상담 날짜와 시간을 선택해주세요.</p>
          <div className="mb-4">
            <label className="block text-[13px] font-semibold text-[#555] mb-2">상담 날짜</label>
            <input
              type="date"
              value={scheduledDate}
              min={today}
              onChange={e => setScheduledDate(e.target.value)}
              className="w-full box-border px-[14px] py-[12px] text-[14px] text-[#333] border-[1.5px] border-gray-200 rounded-[10px] outline-none transition-colors duration-150 focus:border-[#887267]"
            />
          </div>
          <div>
            <label className="block text-[13px] font-semibold text-[#555] mb-2">상담 시간</label>
            {timeSlots.length === 0 ? (
              <p className="text-[13px] text-gray-300">현재 신청 가능한 시간 슬롯이 없습니다.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {timeSlots.map(slot => (
                  <button
                    key={slot.id}
                    onClick={() => setSelectedSlotId(slot.id)}
                    className={[
                      "px-4 py-[10px] rounded-[8px] text-[13px] font-semibold border-[1.5px] transition-all duration-150 cursor-pointer",
                      selectedSlotId === slot.id
                        ? "bg-[#887267] text-white border-[#887267]"
                        : "bg-white text-[#887267] border-[#c4a8e0] hover:bg-[#f5f2f0]",
                    ].join(" ")}
                  >
                    {formatTime(slot.start_time)} ~ {formatTime(slot.end_time)}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* 추가 요청사항 */}
        <div className="bg-white rounded-2xl p-7 mb-4 shadow-[0_2px_12px_rgba(136,114,103,0.06)]">
          <h2 className="m-0 mb-1 text-[16px] font-bold text-[#887267] border-l-[3px] border-[#887267] pl-[10px]">
            추가 요청사항
          </h2>
          <p className="mt-0 mb-4 ml-[13px] text-[12px] text-gray-300">선택사항입니다. 상담 관련 추가 내용을 자유롭게 작성해주세요.</p>
          <textarea
            value={memo}
            onChange={e => setMemo(e.target.value)}
            placeholder="상담 관련 요청사항을 입력해주세요. (선택)"
            rows={5}
            className="w-full box-border px-[14px] py-[14px] text-[14px] text-[#333] border-[1.5px] border-gray-200 rounded-[10px] resize-y outline-none leading-relaxed transition-colors duration-150 focus:border-[#887267]"
          />
        </div>

        {/* 개인정보 동의 */}
        <div className="bg-white rounded-2xl p-7 mb-6 shadow-[0_2px_12px_rgba(136,114,103,0.06)]">
          <h2 className="m-0 mb-1 text-[16px] font-bold text-[#887267] border-l-[3px] border-[#887267] pl-[10px]">
            개인정보 수집 및 이용 동의
          </h2>
          <p className="mt-0 mb-4 ml-[13px] text-[12px] text-gray-300">필수 동의 항목입니다.</p>
          <div className="bg-[#fafafa] border border-gray-100 rounded-[10px] p-4 text-[13px] text-[#555] leading-relaxed whitespace-pre-line mb-4 max-h-[160px] overflow-y-auto">
            {consentPolicy?.content ?? "동의 정책을 불러오는 중..."}
          </div>
          <label className="flex items-center gap-[10px] cursor-pointer px-4 py-[14px] bg-[#f5f2f0] rounded-[10px] border border-[#e8e0dc]">
            <input
              type="checkbox"
              checked={consentChecked}
              onChange={e => setConsentChecked(e.target.checked)}
              className="w-4 h-4 cursor-pointer accent-[#887267]"
            />
            <span className="text-[13px] font-semibold text-[#887267]">
              개인정보 수집 및 이용에 동의합니다 <span className="text-red-500">(필수)</span>
            </span>
          </label>
        </div>

        {/* 신청 버튼 */}
        <button
          onClick={handleSubmit}
          disabled={submitting}
          className={[
            "w-full h-[52px] text-white border-none rounded-xl text-[16px] font-bold transition-colors duration-150 shadow-[0_4px_16px_rgba(136,114,103,0.25)]",
            submitting
              ? "bg-[#b8a39b] cursor-not-allowed"
              : "bg-[#887267] cursor-pointer hover:bg-[#6b5248]",
          ].join(" ")}
        >
          {submitting ? "신청 중..." : "상담 신청하기"}
        </button>
      </div>

      {/* 경고 모달 */}
      {alertModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/40 z-50">
          <div className="bg-white rounded-2xl px-6 py-7 w-[300px] text-center shadow-[0_8px_32px_rgba(0,0,0,0.15)]">
            <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
              <svg viewBox="0 0 24 24" width={24} height={24} fill="none" stroke="#ef4444" strokeWidth={2}>
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
            <p className="text-[14px] text-[#444] mb-5 leading-relaxed">{alertModal}</p>
            <button
              onClick={() => setAlertModal(null)}
              className="w-full h-[42px] bg-[#887267] text-white border-none rounded-lg text-[14px] font-bold cursor-pointer hover:bg-[#6b5248] transition-colors duration-150"
            >
              확인
            </button>
          </div>
        </div>
      )}

      {/* 성공 모달 */}
      {successModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/40 z-50">
          <div className="bg-white rounded-2xl px-6 py-7 w-[300px] text-center shadow-[0_8px_32px_rgba(0,0,0,0.15)]">
            <div className="w-12 h-12 rounded-full bg-[#f5f2f0] flex items-center justify-center mx-auto mb-4">
              <svg viewBox="0 0 24 24" width={24} height={24} fill="none" stroke="#887267" strokeWidth={2}>
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
            </div>
            <p className="text-[16px] font-bold text-[#887267] mb-2">신청 완료!</p>
            <p className="text-[14px] text-[#666] mb-5 leading-relaxed">
              상담 신청이 완료되었습니다.<br />담당자가 확인 후 연락드리겠습니다.
            </p>
            <button
              onClick={() => { setSuccessModal(false); navigate("/"); }}
              className="w-full h-[42px] bg-[#887267] text-white border-none rounded-lg text-[14px] font-bold cursor-pointer hover:bg-[#6b5248] transition-colors duration-150"
            >
              홈으로
            </button>
          </div>
        </div>
      )}
    </div>
  );
}